import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';

export interface WordImportDiagnostic {
  code?: string | null;
  severity?: string | null;
  message?: string | null;
  paragraphIndex?: number | null;
  styleName?: string | null;
}

export interface WordImportResult {
  success: boolean;
  imageCount: number;
  diagnostics: WordImportDiagnostic[];
}

interface ImportWordDocumentDialogProps {
  open: boolean;
  importing: boolean;
  result?: WordImportResult;
  error?: string;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
}

export const ImportWordDocumentDialog: FC<ImportWordDocumentDialogProps> = ({ open, importing, result, error, onClose, onImport }) => {
  const [file, setFile] = useState<File>();

  useEffect(() => {
    if (!open) setFile(undefined);
  }, [open]);

  const diagnostics = result?.diagnostics ?? [];
  const canClose = !importing;

  return (
    <Dialog open={open} onClose={canClose ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>Importer Word-fil</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!result && (
            <>
              <Alert severity="warning">
                Importen erstatter alt innhold i det aktive CMS-dokumentet. Eksisterende innhold kan fortsatt finnes i versjonshistorikken.
              </Alert>
              <Alert severity="info">Bilder importeres i original, liten og stor variant. Figurplassering og figurtekst blir bevart.</Alert>
              <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={importing}>
                Velg Word-fil
                <input
                  hidden
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(event) => setFile(event.target.files?.[0])}
                />
              </Button>
              <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
                {file?.name ?? 'Ingen fil valgt'}
              </Typography>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {result?.success && (
            <Alert severity="success">
              Word-filen er importert. {result.imageCount > 0 && `${result.imageCount} bilder ble importert.`}
            </Alert>
          )}

          {result && !result.success && <Alert severity="error">Dokumentet ble ikke endret fordi Word-filen inneholdt importfeil.</Alert>}

          {diagnostics.length > 0 && (
            <>
              <Typography variant="subtitle2">Importmeldinger</Typography>
              <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
                {diagnostics.map((diagnostic, index) => {
                  const location = [
                    diagnostic.paragraphIndex != null ? `avsnitt ${diagnostic.paragraphIndex + 1}` : null,
                    diagnostic.styleName ? `stil ${diagnostic.styleName}` : null,
                  ]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <ListItem key={`${diagnostic.code ?? 'WTC'}-${index}`} disableGutters>
                      <ListItemText
                        primary={`${diagnostic.severity ?? 'Info'} ${diagnostic.code ?? ''}: ${diagnostic.message ?? ''}`}
                        secondary={location || undefined}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {importing && <CircularProgress title="Importerer Word-fil" size={25} />}
        {!result && (
          <Button disabled={!canClose} onClick={onClose}>
            Avbryt
          </Button>
        )}
        {!result && (
          <Button variant="contained" disabled={!file || importing} onClick={() => file && onImport(file)}>
            Erstatt innhold
          </Button>
        )}
        {result && !result.success && (
          <Button variant="contained" disabled={!canClose} onClick={onClose}>
            Lukk
          </Button>
        )}
        {result?.success && (
          <Button variant="contained" onClick={() => window.location.reload()}>
            Vis importert dokument
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
